import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  interruptQueue,
  pauseAllQueues,
  pauseQueue,
  resumeQueue,
  stopQueue,
  type InterruptDisposition,
  type OrgPauseResult,
  type QueueControlResult,
} from "./api";

// Операторские контролы очереди (пауза/снятие/прерывание/стоп/org-wide).
// После любого действия инвалидируем queue (состояние пауз + активная
// задача) и tasks (статусы могли смениться при прерывании активной).
// Инвалидация по смысловому префиксу матчит запись любого org-скоупа
// (см. `src/query/scope.ts`).
function useInvalidateQueueAndTasks(): () => void {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ["queue"] });
    void queryClient.invalidateQueries({ queryKey: ["tasks"] });
  };
}

export function usePauseQueue(): ReturnType<
  typeof useMutation<QueueControlResult, Error, void>
> {
  const invalidate = useInvalidateQueueAndTasks();
  return useMutation<QueueControlResult, Error, void>({
    mutationFn: () => pauseQueue(),
    onSuccess: invalidate,
  });
}

export function useResumeQueue(): ReturnType<
  typeof useMutation<QueueControlResult, Error, void>
> {
  const invalidate = useInvalidateQueueAndTasks();
  return useMutation<QueueControlResult, Error, void>({
    mutationFn: () => resumeQueue(),
    onSuccess: invalidate,
  });
}

export function useInterruptQueue(): ReturnType<
  typeof useMutation<QueueControlResult, Error, InterruptDisposition>
> {
  const invalidate = useInvalidateQueueAndTasks();
  return useMutation<QueueControlResult, Error, InterruptDisposition>({
    mutationFn: (disposition) => interruptQueue(disposition),
    onSuccess: invalidate,
  });
}

export function useStopQueue(): ReturnType<
  typeof useMutation<QueueControlResult, Error, InterruptDisposition>
> {
  const invalidate = useInvalidateQueueAndTasks();
  return useMutation<QueueControlResult, Error, InterruptDisposition>({
    mutationFn: (disposition) => stopQueue(disposition),
    onSuccess: invalidate,
  });
}

export function usePauseAllQueues(): ReturnType<
  typeof useMutation<OrgPauseResult, Error, void>
> {
  const invalidate = useInvalidateQueueAndTasks();
  return useMutation<OrgPauseResult, Error, void>({
    mutationFn: () => pauseAllQueues(),
    onSuccess: invalidate,
  });
}
